import React, { useState } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  FormControl,
  FormLabel,
  Input,
  Button,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Card,
  CardBody,
  Badge,
  Icon,
  Divider,
  Alert,
  AlertIcon,
} from "@chakra-ui/react";
import { FaHome, FaDollarSign, FaCalendarAlt, FaTrophy } from "react-icons/fa";
import { NavbarWithCallToAction } from "../../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { SEOHead } from "../../../components/SEOHead";
import { LeadCaptureForm } from "../../../components/calculator/LeadCaptureForm";
import { CalculatorType } from "../../../generated/graphql";

const RefinanceSavingsCalculator: React.FC = () => {
  const [currentLoan, setCurrentLoan] = useState({
    balance: 400000,
    rate: 5.5,
    term: 25,
  });

  const [newLoan, setNewLoan] = useState({
    rate: 4.5,
    fees: 1500,
  });

  const [results, setResults] = useState<any>(null);

  const calculate = () => {
    // Current loan monthly payment
    const currentMonthlyRate = currentLoan.rate / 100 / 12;
    const currentMonths = currentLoan.term * 12;
    const currentMonthlyPayment =
      (currentLoan.balance * currentMonthlyRate * Math.pow(1 + currentMonthlyRate, currentMonths)) /
      (Math.pow(1 + currentMonthlyRate, currentMonths) - 1);

    // New loan monthly payment
    const newMonthlyRate = newLoan.rate / 100 / 12;
    const newMonths = currentLoan.term * 12;
    const newMonthlyPayment =
      (currentLoan.balance * newMonthlyRate * Math.pow(1 + newMonthlyRate, newMonths)) /
      (Math.pow(1 + newMonthlyRate, newMonths) - 1);

    // Savings calculations
    const monthlySavings = currentMonthlyPayment - newMonthlyPayment;
    const annualSavings = monthlySavings * 12;

    // Total interest calculations
    const currentTotalInterest = currentMonthlyPayment * currentMonths - currentLoan.balance;
    const newTotalInterest = newMonthlyPayment * newMonths - currentLoan.balance;
    const totalInterestSaved = currentTotalInterest - newTotalInterest - newLoan.fees;

    // Break-even calculation (months to recover refinance fees)
    const breakEvenMonths = Math.ceil(newLoan.fees / monthlySavings);

    setResults({
      currentMonthlyPayment: currentMonthlyPayment.toFixed(0),
      newMonthlyPayment: newMonthlyPayment.toFixed(0),
      monthlySavings: monthlySavings.toFixed(0),
      annualSavings: annualSavings.toFixed(0),
      totalInterestSaved: totalInterestSaved.toFixed(0),
      breakEvenMonths: breakEvenMonths,
      currentTotalInterest: currentTotalInterest.toFixed(0),
      newTotalInterest: newTotalInterest.toFixed(0),
    });
  };

  const reset = () => {
    setCurrentLoan({ balance: 400000, rate: 5.5, term: 25 });
    setNewLoan({ rate: 4.5, fees: 1500 });
    setResults(null);
  };

  return (
    <Box minH="100vh" bg="gray.50">
      <SEOHead
        pagePath="/calculators/refinance"
        fallbackTitle="Refinance Savings Calculator"
        fallbackDescription="Calculate how much you could save by refinancing your home loan"
      />
      <NavbarWithCallToAction />

      {/* Hero Section */}
      <Box
        bgGradient="linear(to-r, green.500, teal.400)"
        color="white"
        py={16}
        position="relative"
        overflow="hidden"
      >
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <VStack spacing={4} textAlign="center">
            <Icon as={FaHome} boxSize={12} />
            <Heading size="2xl" fontWeight="bold">
              Refinance Savings Calculator
            </Heading>
            <Text fontSize="xl" maxW="2xl">
              See how much you could save by refinancing to a better interest rate
            </Text>
          </VStack>
        </Container>
      </Box>

      {/* Three-Column Split Layout */}
      <Container maxW="container.xl" py={12}>
        <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={8} mb={8}>
          {/* Column 1: Current Loan */}
          <Card borderTop="4px solid" borderColor="red.400">
            <CardBody>
              <VStack spacing={6} align="stretch">
                <HStack justify="space-between">
                  <Heading size="md" color="red.600">
                    Current Loan
                  </Heading>
                  <Badge colorScheme="red">Existing</Badge>
                </HStack>

                <FormControl>
                  <FormLabel>Loan Balance</FormLabel>
                  <Input
                    type="number"
                    value={currentLoan.balance}
                    onChange={(e) =>
                      setCurrentLoan((prev) => ({ ...prev, balance: parseFloat(e.target.value) || 0 }))
                    }
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Interest Rate (%)</FormLabel>
                  <Input
                    type="number"
                    step="0.01"
                    value={currentLoan.rate}
                    onChange={(e) =>
                      setCurrentLoan((prev) => ({ ...prev, rate: parseFloat(e.target.value) || 0 }))
                    }
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Years Remaining</FormLabel>
                  <Input
                    type="number"
                    value={currentLoan.term}
                    onChange={(e) =>
                      setCurrentLoan((prev) => ({ ...prev, term: parseFloat(e.target.value) || 0 }))
                    }
                  />
                </FormControl>

                {results && (
                  <Box bg="red.50" p={4} borderRadius="md">
                    <Stat>
                      <StatLabel>Monthly Payment</StatLabel>
                      <StatNumber color="red.600">
                        ${parseInt(results.currentMonthlyPayment).toLocaleString()}
                      </StatNumber>
                      <StatHelpText>Current rate: {currentLoan.rate}%</StatHelpText>
                    </Stat>
                  </Box>
                )}
              </VStack>
            </CardBody>
          </Card>

          {/* Column 2: New Loan */}
          <Card borderTop="4px solid" borderColor="green.400">
            <CardBody>
              <VStack spacing={6} align="stretch">
                <HStack justify="space-between">
                  <Heading size="md" color="green.600">
                    New Loan
                  </Heading>
                  <Badge colorScheme="green">Refinanced</Badge>
                </HStack>

                <FormControl>
                  <FormLabel>New Interest Rate (%)</FormLabel>
                  <Input
                    type="number"
                    step="0.01"
                    value={newLoan.rate}
                    onChange={(e) =>
                      setNewLoan((prev) => ({ ...prev, rate: parseFloat(e.target.value) || 0 }))
                    }
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Shop around for best rates
                  </Text>
                </FormControl>

                <FormControl>
                  <FormLabel>Refinance Fees ($)</FormLabel>
                  <Input
                    type="number"
                    value={newLoan.fees}
                    onChange={(e) =>
                      setNewLoan((prev) => ({ ...prev, fees: parseFloat(e.target.value) || 0 }))
                    }
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    Discharge, application, valuation fees
                  </Text>
                </FormControl>

                <Box>
                  <Text fontSize="sm" color="gray.600" mb={2}>
                    Loan Details (from current):
                  </Text>
                  <VStack spacing={1} align="stretch" fontSize="sm">
                    <HStack justify="space-between">
                      <Text color="gray.600">Balance:</Text>
                      <Text fontWeight="semibold">
                        ${currentLoan.balance.toLocaleString()}
                      </Text>
                    </HStack>
                    <HStack justify="space-between">
                      <Text color="gray.600">Term:</Text>
                      <Text fontWeight="semibold">{currentLoan.term} years</Text>
                    </HStack>
                  </VStack>
                </Box>

                {results && (
                  <Box bg="green.50" p={4} borderRadius="md">
                    <Stat>
                      <StatLabel>Monthly Payment</StatLabel>
                      <StatNumber color="green.600">
                        ${parseInt(results.newMonthlyPayment).toLocaleString()}
                      </StatNumber>
                      <StatHelpText>New rate: {newLoan.rate}%</StatHelpText>
                    </Stat>
                  </Box>
                )}
              </VStack>
            </CardBody>
          </Card>

          {/* Column 3: Savings Comparison */}
          <Card borderTop="4px solid" borderColor="blue.400">
            <CardBody>
              <VStack spacing={6} align="stretch">
                <HStack justify="space-between">
                  <Heading size="md" color="blue.600">
                    Your Savings
                  </Heading>
                  <Icon as={FaTrophy} color="blue.500" boxSize={6} />
                </HStack>

                {results ? (
                  <>
                    <Box bg="blue.50" p={4} borderRadius="md">
                      <Stat textAlign="center">
                        <StatLabel>Monthly Savings</StatLabel>
                        <StatNumber fontSize="3xl" color="blue.600">
                          ${parseInt(results.monthlySavings).toLocaleString()}
                        </StatNumber>
                        <StatHelpText>Per month</StatHelpText>
                      </Stat>
                    </Box>

                    <Divider />

                    <VStack spacing={4} align="stretch">
                      <HStack justify="space-between">
                        <HStack>
                          <Icon as={FaDollarSign} color="green.500" />
                          <Text fontSize="sm">Annual Savings:</Text>
                        </HStack>
                        <Text fontWeight="bold" fontSize="lg" color="green.600">
                          ${parseInt(results.annualSavings).toLocaleString()}
                        </Text>
                      </HStack>

                      <HStack justify="space-between">
                        <HStack>
                          <Icon as={FaCalendarAlt} color="orange.500" />
                          <Text fontSize="sm">Break-even:</Text>
                        </HStack>
                        <Text fontWeight="bold" fontSize="lg" color="orange.600">
                          {results.breakEvenMonths} months
                        </Text>
                      </HStack>

                      <Divider />

                      <Box>
                        <Text fontSize="sm" fontWeight="semibold" mb={2} color="gray.700">
                          Total Interest Over Life of Loan:
                        </Text>
                        <HStack justify="space-between" mb={1}>
                          <Text fontSize="xs" color="gray.600">
                            Current:
                          </Text>
                          <Text fontSize="sm" fontWeight="semibold" color="red.600">
                            ${parseInt(results.currentTotalInterest).toLocaleString()}
                          </Text>
                        </HStack>
                        <HStack justify="space-between" mb={2}>
                          <Text fontSize="xs" color="gray.600">
                            New:
                          </Text>
                          <Text fontSize="sm" fontWeight="semibold" color="green.600">
                            ${parseInt(results.newTotalInterest).toLocaleString()}
                          </Text>
                        </HStack>
                        <Box bg="green.100" p={2} borderRadius="md">
                          <HStack justify="space-between">
                            <Text fontSize="sm" fontWeight="bold" color="green.800">
                              Total Saved:
                            </Text>
                            <Text fontSize="lg" fontWeight="bold" color="green.700">
                              ${parseInt(results.totalInterestSaved).toLocaleString()}
                            </Text>
                          </HStack>
                        </Box>
                      </Box>
                    </VStack>

                    {parseFloat(results.totalInterestSaved) > 0 && (
                      <Alert status="success" borderRadius="md">
                        <AlertIcon />
                        <Text fontSize="xs">
                          Refinancing could save you significant money over the loan term!
                        </Text>
                      </Alert>
                    )}
                  </>
                ) : (
                  <VStack spacing={4} justify="center" py={8} color="gray.400">
                    <Icon as={FaTrophy} boxSize={12} />
                    <Text fontSize="sm" textAlign="center">
                      Calculate to see your potential savings
                    </Text>
                  </VStack>
                )}
              </VStack>
            </CardBody>
          </Card>
        </SimpleGrid>

        <HStack spacing={4} justify="center">
          <Button colorScheme="green" size="lg" onClick={calculate} px={12}>
            Calculate Savings
          </Button>
          <Button variant="outline" size="lg" onClick={reset}>
            Reset
          </Button>
        </HStack>

        {results && (
          <>
            <Box mt={8} bg="blue.50" p={6} borderRadius="md" borderLeft="4px solid" borderColor="blue.500">
              <Text fontSize="sm" color="gray.700">
                <strong>Important:</strong> This calculator provides estimates only. Actual savings will
                depend on your specific loan terms, fees, and lender policies. Always compare multiple
                lenders and consider all costs including exit fees, application fees, and potential loss of
                features like offset accounts. Speak with a mortgage broker for personalised advice.
              </Text>
            </Box>

            <Box mt={8} maxW="md" mx="auto">
              <LeadCaptureForm
                calculatorType={CalculatorType.RefinanceSavings}
                calculatorName="Refinance Savings Calculator"
                calculatorPath="/calculators/refinance-savings"
                inputData={{ currentLoan, newLoan }}
                resultData={results}
                hasResults={!!results}
                accentColor="green"
              />
            </Box>
          </>
        )}
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default RefinanceSavingsCalculator;
